import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined';
import StarsOutlinedIcon from '@mui/icons-material/StarsOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import { GuideStatCard } from '@/features/guide/components/GuideStatCard';
import { DashboardSectionHeader } from '@/shared/components/dashboard/DashboardSectionHeader';
import { dashboardPanelSx } from '@/shared/styles/surfaces';
import { useReferral, useReferrals } from '../hooks/useGrowth';
import type { ReferralRow, ReferralStats, ReferralStatus } from '../types';
import { RewardChip } from './RewardChip';

const fmt = (date: string | null) => (date ? dayjs(date).format('D MMM YYYY') : '—');
const STATUSES: ReferralStatus[] = ['REGISTERED', 'ONBOARDING', 'APPROVED', 'REJECTED'];

export function ReferralStatsGrid({ stats, loading }: { stats?: ReferralStats; loading: boolean }) {
  const { t } = useTranslation('rewards');
  const cards = [
    { label: t('stats.total'), value: stats?.total, icon: <GroupsOutlinedIcon />, accent: 'primary.main' as const },
    { label: t('stats.registered'), value: stats?.registered, icon: <HowToRegOutlinedIcon />, accent: 'info.main' as const },
    { label: t('stats.onboarding'), value: stats?.onboarding, icon: <PendingActionsOutlinedIcon />, accent: 'warning.main' as const },
    { label: t('stats.approved'), value: stats?.approved, icon: <VerifiedOutlinedIcon />, accent: 'success.main' as const },
    {
      label: t('stats.pendingRewards'),
      value: stats?.pendingRewards,
      icon: <HourglassTopOutlinedIcon />,
      accent: 'warning.main' as const,
      subtitle: stats?.pendingCredits ? t('stats.pendingSubtitle', { credits: stats.pendingCredits }) : undefined,
    },
    { label: t('stats.creditsEarned'), value: stats?.creditsEarned, icon: <StarsOutlinedIcon />, accent: 'secondary.main' as const },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(6, minmax(0, 1fr))' },
      }}
    >
      {cards.map((card) => (
        <GuideStatCard
          key={card.label}
          label={card.label}
          value={(card.value ?? 0).toLocaleString('en-IN')}
          loading={loading}
          icon={card.icon}
          accent={card.accent}
          subtitle={card.subtitle}
        />
      ))}
    </Box>
  );
}

function ReferralDetailDrawer({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { t } = useTranslation('rewards');
  const { data, isLoading, isError } = useReferral(id);

  const steps = data
    ? [
        { label: t('history.registeredOn'), at: data.registeredAt, done: true },
        { label: t('history.onboardingDone'), at: data.onboardingCompletedAt, done: Boolean(data.onboardingCompletedAt) || data.status === 'APPROVED' },
        { label: t('history.approvedOn'), at: data.approvedAt, done: data.status === 'APPROVED' },
      ]
    : [];

  return (
    <Drawer anchor="right" open={Boolean(id)} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" fontWeight={800}>
          {t('history.detailTitle')}
        </Typography>
        <IconButton onClick={onClose} aria-label={t('share.close')}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>
      <Divider />
      <Box sx={{ p: 3, display: 'grid', gap: 2.5 }}>
        {isLoading && <Skeleton variant="rounded" height={180} />}
        {isError && <Alert severity="error">{t('common.error')}</Alert>}
        {data && (
          <>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {data.name}
              </Typography>
              {data.guideId && (
                <Typography sx={{ fontFamily: 'ui-monospace, monospace', color: 'text.secondary', fontWeight: 700 }}>
                  {data.guideId}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                <RewardChip label={t(`status.referral.${data.status}`)} status={data.status} />
                <RewardChip label={t(`status.source.${data.source}`)} tone="neutral" />
              </Box>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('history.timeline')}
              </Typography>
              <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 1, display: 'grid', gap: 1.5 }}>
                {steps.map((step) => (
                  <Box component="li" key={step.label} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                    {step.done ? (
                      <CheckCircleRoundedIcon sx={{ color: 'success.main' }} fontSize="small" />
                    ) : (
                      <RadioButtonUncheckedRoundedIcon sx={{ color: 'grey.400' }} fontSize="small" />
                    )}
                    <Box>
                      <Typography fontWeight={600} fontSize="0.9rem">
                        {step.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {step.done ? fmt(step.at) : t('history.notYet')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {data.status === 'REJECTED' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t('history.rejectedNote')}
                </Alert>
              )}
            </Box>

            <Paper elevation={0} sx={{ ...dashboardPanelSx, p: 2 }}>
              <Typography variant="overline" color="text.secondary" fontWeight={700}>
                {t('history.rewardLine')}
              </Typography>
              {data.rewardStatus ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                  <RewardChip label={t(`status.reward.${data.rewardStatus}`)} status={data.rewardStatus} />
                  <Typography fontWeight={800}>
                    {(data.creditsEarned || data.pendingCredits).toLocaleString('en-IN')} {t('common.credits')}
                  </Typography>
                </Box>
              ) : (
                <Typography color="text.secondary" fontSize="0.9rem">
                  {t('history.noReward')}
                </Typography>
              )}
            </Paper>
          </>
        )}
      </Box>
    </Drawer>
  );
}

function ReferralMobileRow({ row, onOpen }: { row: ReferralRow; onOpen: () => void }) {
  const { t } = useTranslation('rewards');
  return (
    <Box
      component="button"
      onClick={onOpen}
      sx={{
        all: 'unset',
        boxSizing: 'border-box',
        width: '100%',
        cursor: 'pointer',
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'grid',
        gap: 0.75,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
        <Typography fontWeight={700}>{row.name}</Typography>
        <Typography fontWeight={800} color={row.creditsEarned ? 'secondary.dark' : 'text.secondary'}>
          {row.creditsEarned ? `+${row.creditsEarned}` : '—'}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        <RewardChip label={t(`status.referral.${row.status}`)} status={row.status} />
        {row.rewardStatus && <RewardChip label={t(`status.reward.${row.rewardStatus}`)} status={row.rewardStatus} />}
        <Typography variant="caption" color="text.secondary">
          {fmt(row.registeredAt)}
        </Typography>
      </Box>
    </Box>
  );
}

export function ReferralHistory() {
  const { t } = useTranslation('rewards');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ReferralStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const query = {
    page: page + 1,
    limit,
    search: search.trim() || undefined,
    status,
    from: from || undefined,
    to: to ? dayjs(to).endOf('day').toISOString() : undefined,
  };
  const { data, isLoading, isError, refetch, isFetching } = useReferrals(query);
  const filtered = Boolean(search || status || from || to);

  const resetPage = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  return (
    <Box>
      <DashboardSectionHeader title={t('history.title')} />
      <Paper elevation={0} sx={{ ...dashboardPanelSx, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1fr 1fr auto' }, alignItems: 'center' }}>
          <TextField
            size="small"
            value={search}
            onChange={(e) => resetPage(setSearch)(e.target.value)}
            placeholder={t('history.search')}
            inputProps={{ 'aria-label': t('history.search') }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField size="small" select label={t('history.status')} value={status} onChange={(e) => resetPage(setStatus)(e.target.value as ReferralStatus | '')}>
            <MenuItem value="">{t('history.allStatuses')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`status.referral.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" type="date" label={t('history.from')} value={from} onChange={(e) => resetPage(setFrom)(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label={t('history.to')} value={to} onChange={(e) => resetPage(setTo)(e.target.value)} InputLabelProps={{ shrink: true }} />
          {filtered && (
            <Button
              size="small"
              onClick={() => {
                setSearch('');
                setStatus('');
                setFrom('');
                setTo('');
                setPage(0);
              }}
            >
              {t('history.clear')}
            </Button>
          )}
        </Box>
        <Divider />

        {isError ? (
          <Alert severity="error" sx={{ m: 2 }} action={<Button onClick={() => refetch()}>{t('page.retry')}</Button>}>
            {t('common.error')}
          </Alert>
        ) : isLoading ? (
          <Box sx={{ p: 2, display: 'grid', gap: 1 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} variant="rounded" height={44} />
            ))}
          </Box>
        ) : !data?.items.length ? (
          <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
            <GroupsOutlinedIcon sx={{ fontSize: 40, color: 'grey.400' }} />
            <Typography fontWeight={700} sx={{ mt: 1 }}>
              {filtered ? t('history.emptyFiltered') : t('history.emptyTitle')}
            </Typography>
            {!filtered && (
              <Typography variant="body2" color="text.secondary">
                {t('history.emptyBody')}
              </Typography>
            )}
          </Box>
        ) : isMobile ? (
          <Box sx={{ opacity: isFetching ? 0.6 : 1 }}>
            {data.items.map((row) => (
              <ReferralMobileRow key={row.id} row={row} onOpen={() => setOpenId(row.id)} />
            ))}
          </Box>
        ) : (
          <TableContainer sx={{ opacity: isFetching ? 0.6 : 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {(['name', 'guideId', 'registered', 'source', 'onboarding', 'approval', 'reward', 'credits'] as const).map((col) => (
                    <TableCell key={col} align={col === 'credits' ? 'right' : 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {t(`history.columns.${col}`)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.items.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    tabIndex={0}
                    onClick={() => setOpenId(row.id)}
                    onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpenId(row.id)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell sx={{ fontWeight: 600 }}>{row.name}</TableCell>
                    <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}>{row.guideId ?? '—'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(row.registeredAt)}</TableCell>
                    <TableCell>{t(`status.source.${row.source}`)}</TableCell>
                    <TableCell>
                      <RewardChip label={t(`status.onboarding.${row.onboardingStatus}`)} status={row.onboardingStatus} />
                    </TableCell>
                    <TableCell>
                      <RewardChip label={t(`status.referral.${row.status}`)} status={row.status} />
                    </TableCell>
                    <TableCell>
                      {row.rewardStatus ? <RewardChip label={t(`status.reward.${row.rewardStatus}`)} status={row.rewardStatus} /> : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800 }}>
                      {row.creditsEarned ? row.creditsEarned.toLocaleString('en-IN') : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {Boolean(data?.total) && (
          <TablePagination
            component="div"
            count={data?.total ?? 0}
            page={page}
            onPageChange={(_e, next) => setPage(next)}
            rowsPerPage={limit}
            onRowsPerPageChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        )}
      </Paper>
      <ReferralDetailDrawer id={openId} onClose={() => setOpenId(null)} />
    </Box>
  );
}
